import { useState, useCallback, Children } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";

import { useActiveLink } from "@/routes/hooks/use-active-link";
import { RouterLink } from "@/routes/components";

import SvgColor from "@/components/svg-color";
import Iconify from "@/components/iconify";
import { navVerticalConfig } from "@/components/nav-section/config";
import {
  StyledItem,
  StyledIcon,
  StyledDotIcon,
  StyledSubheader,
} from "@/components/nav-section/vertical/styles";

const navConfig = navVerticalConfig({});

export const Icon = ({ src }) => (
  <SvgColor src={src} sx={{ width: 1, height: 1 }} />
);

export const Subtitle = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
    {children}
  </Typography>
);

export function NavSubItem({ title, path }) {
  const active = useActiveLink(path, false);

  return (
    <Link component={RouterLink} href={path} underline="none" color="inherit">
      <StyledItem disableGutters active={active} depth={2} config={navConfig}>
        <StyledIcon size={navConfig.iconSize}>
          <StyledDotIcon active={active} />
        </StyledIcon>
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            noWrap: true,
            typography: "body2",
            textTransform: "capitalize",
            fontWeight: active ? "fontWeightSemiBold" : "fontWeightMedium",
          }}
        />
      </StyledItem>
    </Link>
  );
}

export function NavItem({
  title,
  subtitle,
  icon,
  path,
  activePath,
  externalLink,
  children,
}) {
  const hasChildren = Children.toArray(children).filter(Boolean).length > 0;
  const active = useActiveLink(activePath ?? path, hasChildren);
  const [open, setOpen] = useState(active);

  const displayTitle = subtitle ? (
    <Stack>
      <span>{title}</span>
      <Subtitle>{subtitle}</Subtitle>
    </Stack>
  ) : (
    title
  );

  const content = (
    <StyledItem
      disableGutters
      active={active}
      depth={1}
      config={navConfig}
      onClick={hasChildren ? () => setOpen(!open) : undefined}
    >
      {icon && <StyledIcon size={navConfig.iconSize}>{icon}</StyledIcon>}
      <ListItemText
        primary={displayTitle}
        primaryTypographyProps={{
          noWrap: true,
          typography: "body2",
          textTransform: "capitalize",
          fontWeight: active ? "fontWeightSemiBold" : "fontWeightMedium",
        }}
      />
      {hasChildren && (
        <Iconify
          width={16}
          icon={
            open ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"
          }
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  if (externalLink) {
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        color="inherit"
      >
        {content}
      </Link>
    );
  }

  if (hasChildren) {
    return (
      <>
        {content}
        <Collapse in={open} unmountOnExit>
          {children}
        </Collapse>
      </>
    );
  }

  return (
    <Link component={RouterLink} href={path} underline="none" color="inherit">
      {content}
    </Link>
  );
}

export function NavSection({ subheader, children }) {
  const [open, setOpen] = useState(true);
  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);

  const items = Children.toArray(children).filter(Boolean);

  if (items.length === 0) return null;

  return (
    <List disablePadding sx={{ px: 2 }}>
      {subheader ? (
        <>
          <StyledSubheader
            disableGutters
            disableSticky
            onClick={handleToggle}
            config={navConfig}
          >
            {subheader}
          </StyledSubheader>
          <Collapse in={open}>{items}</Collapse>
        </>
      ) : (
        items
      )}
    </List>
  );
}
